<script setup lang="ts">
import { throttle } from 'throttle-debounce'

type Props = {
  showTop?: boolean
  showBottom?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showTop: true,
  showBottom: true,
})

const scrollInnerElement = useTemplateRef('scrollInnerElement')

const showTopGradient = ref(false)
const showBottomGradient = ref(false)

const resizeObserver = ref<ResizeObserver | null>(null)

const componentClasses = computed(() => {
  return [
    'app-scroll-container',
    {
      'app-scroll-container--top': showTopGradient.value && props.showTop,
      'app-scroll-container--bottom':
        showBottomGradient.value && props.showBottom,
    },
  ]
})

const onUpdate = throttle(100, () => {
  if (!scrollInnerElement.value) return

  const { scrollHeight, clientHeight } = scrollInnerElement.value
  const scrollTop = Math.ceil(scrollInnerElement.value.scrollTop || 0)

  showTopGradient.value = scrollTop > 0
  showBottomGradient.value = scrollTop < scrollHeight - clientHeight
})

const attachObserver = () => {
  resizeObserver.value = new ResizeObserver(onUpdate)

  if (resizeObserver.value && scrollInnerElement.value) {
    resizeObserver.value.observe(scrollInnerElement.value)
  }
}

const detachObserver = () => {
  if (resizeObserver.value && scrollInnerElement.value) {
    resizeObserver.value.unobserve(scrollInnerElement.value)
  }
}

onMounted(() => {
  if (!props.showTop && !props.showBottom) return

  scrollInnerElement.value?.addEventListener('scroll', onUpdate)

  attachObserver()
})

onBeforeUnmount(() => {
  scrollInnerElement.value?.removeEventListener('scroll', onUpdate)

  detachObserver()
})
</script>

<template>
  <div :class="componentClasses">
    <div
      ref="scrollInnerElement"
      class="app-scroll-container__inner"
      data-test-id="scroll-container-inner"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/ui-layer/styles/mixins' as *;

.app-scroll-container {
  position: relative;

  &__inner {
    height: inherit;
    max-height: inherit;
    overflow: auto;
    @include scrollbar();
  }

  &::before,
  &::after {
    position: absolute;
    right: 4px;
    left: 0;
    height: 48px;
    pointer-events: none;
    visibility: hidden;
    content: '';
    background: var(--ui-gradient-white);
    opacity: 0;
    transition: opacity 0.2s ease-in, visibility 0.2s ease-in;
  }

  &::before {
    top: 0;
    transform: scaleY(-1);
  }

  &::after {
    bottom: 0;
  }

  &--top::before,
  &--bottom::after {
    pointer-events: auto;
    visibility: visible;
    opacity: 1;
  }
}
</style>
